<script lang="ts">
    import { fade } from 'svelte/transition';
    import QuestionModal from "./questionModal.svelte";
    import { createEventDispatcher, getContext } from "svelte";
    import type { Section } from "../Model/Section";
    import SectionForm from "./sectionForm.svelte";
    import type { Readable } from "svelte/store";
    import type { Question } from "../Model/Question";
    import { Button, Icon } from "sveltestrap";
    import { sectionsFactory } from "../services";
    import QuestionTypeLabel from "./QuestionTypeLabel.svelte";
    import DeployIcon from "./DeployIcon.svelte";
    import QuestionResume from "./QuestionResume.svelte";
    import { widthState } from '../Directives/widthState';
    import Editable from './FormPreview/Editable.svelte';
    import Watch from './Watch.svelte';

    export let question: Question = null;
    export let allowSubsections: boolean = true;

    const dispatch = createEventDispatcher();
    const expanAll: Readable<boolean> = getContext('expandAll');
    const readonly: Readable<boolean> = getContext('readonly');

    let editQuestion = null;
    let sections: Readable<Section[]>;
    let openBody = false;

    $: setupComponent(question);
    $: openBody = $expanAll;

    function setupComponent(question: Question): void {
        sections = question.sectionsReadable;
    }

    function newSection() {
        const section = sectionsFactory.createNewSection(question.form);
        section.parentId = question.id;
        question.addSection(section);
    }

    function onSectionUp({ detail: section }: CustomEvent<Section>) {
        question.moveSectionUp(section);
    }

    function onSectionDown({ detail: section }: CustomEvent<Section>) {
        question.moveSectionDown(section);
    }

    async function onRemove() {
        if (!confirm(`¿Esta seguro de querer eliminar la pregunta?`)) {
            return;
        }
        question.remove();
    }

    function onRemoveSection({ detail: section }: CustomEvent<Section>): void {
        question.quitSection(section.id);
    }

    function onEdit(): void {
        editQuestion = question;
    }

    function onDown(): void {
        dispatch("downPosition", question);
    }

    function onUp(): void {
        dispatch("upPosition", question);
    }

    function onEditClose(): void {
        question = question;
    }

    function onToggleBody() {
        openBody = !openBody;
    }

</script>

<style lang="scss">

    .question {
        margin-top: 1rem;
        padding-left: 1rem;
        box-sizing: border-box;
    }

    .heading {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.5rem;

        .controls {
            opacity: 0;
            transition: opacity 200ms;
        }

        &:hover {

            .controls {
                opacity: 1;
            }

        }
    }

    .name {
        font-size: 1rem;
        font-weight: bold;
        color: #777;

        &.required {
            color: #000;
        }
    }

    .body {
        padding: 1rem;
    }

    .sections {
        margin: 1rem -1rem 0 -1rem;
    }

</style>

<div class="question" use:widthState={question.width}>
    <div class="border border-end-0 rounded-start">
        <div class="heading bg-light" class:border-bottom={openBody} on:click={onToggleBody}>
            <div class="name" class:required={question.forced}>
                <DeployIcon isDeployed={openBody} />
                <Editable store={question.name} />
                <Watch store={question.forced} let:value={forced}>
                    {#if forced}
                        <Icon name="shield-fill-exclamation" class="text-warning" />
                    {/if}
                </Watch>
                (<QuestionTypeLabel type={question.type} />)
            </div>
            <div class="controls btn-group btn-group-sm">
                <button
                    type="button"
                    class="btn btn-primary"
                    disabled={$readonly}
                    on:click|stopPropagation={onEdit}><Icon name="pencil" /></button>
                <button
                    type="button"
                    class="btn btn-outline-primary"
                    disabled={$readonly}
                    on:click|stopPropagation={onDown}><Icon name="arrow-down" /></button>
                <button
                    type="button"
                    class="btn btn-outline-primary"
                    disabled={$readonly}
                    on:click|stopPropagation={onUp}><Icon name="arrow-up" /></button>
                <button
                    type="button"
                    class="btn btn-danger"
                    disabled={$readonly}
                    on:click|stopPropagation={onRemove}><Icon name="x" /></button>
            </div>
        </div>
        {#if openBody}
        <div class="body" transition:fade={{ duration: 200 }}>
            <QuestionResume {question} />
            {#if allowSubsections && question.type == 'bool'}
                {#if $sections.length === 0}
                    <div class="mt-3 d-flex justify-content-end">
                        <Button type="button" size="sm" on:click={newSection} disabled={$readonly}>
                            <Icon name="plus" /> Añadir sección
                        </Button>
                    </div>
                {/if}
                {#if $sections.length}
                <div class="sections">
                    {#each $sections as section}
                        <SectionForm
                            {section}
                            on:upPosition={onSectionUp}
                            on:downPosition={onSectionDown}
                            on:remove={onRemoveSection} />
                    {/each}
                </div>
                {/if}
            {/if}
        </div>
        {/if}
    </div>
</div>

<QuestionModal question={editQuestion} on:close={onEditClose} />
